// frontend/src/pages/Register.jsx
import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faLock,
  faPhone,
  faMapMarkerAlt,
  faTractor,
  faBuilding,
  faArrowRight,
  faSpinner,
  faCheckCircle,
  faUserTie,
  faBriefcase,
  faUsers,
  faShieldAlt,
  faCode,
  faHandshake,
  faLightbulb,
  faClipboardList,
  faUserPlus,
  faUserCog,
  faGlobe,
  faShip,
  faFileAlt,
  faCertificate,
  faBox,
  faTag,
  faMoneyBillTransfer,
  faClock,
  faExclamationTriangle,
  faStore,
  faWarehouse,
  faTruck,
  faDollarSign,
  faPercent,
  faCalendarCheck,
  faWhatsapp,
  faPhoneAlt,
  faCity,
  faLocationDot,
  faIdCard,
  faImage,
  faCamera,
  faUpload,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";
import SEO from "../components/SEO/SEO";
import "../styles/auth.scss";

// ============================================
// TEAM ROLES CONFIGURATION
// ============================================
const TEAM_ROLES = [
  {
    value: "sale_representative",
    label: "Sales Representative",
    icon: faHandshake,
    description: "Handles sales and customer relationships",
  },
  {
    value: "advisor",
    label: "Advisor",
    icon: faLightbulb,
    description: "Provides expert advice to customers",
  },
  {
    value: "coordinator",
    label: "Coordinator",
    icon: faClipboardList,
    description: "Coordinates operations and logistics",
  },
  {
    value: "assistant_advisor",
    label: "Assistant Advisor",
    icon: faUserPlus,
    description: "Supports advisors with customer needs",
  },
  {
    value: "assistant_coordinator",
    label: "Assistant Coordinator",
    icon: faUserCog,
    description: "Supports coordinators with operations",
  },
  {
    value: "manager",
    label: "Manager",
    icon: faBriefcase,
    description: "Manages team and operations",
  },
  {
    value: "assistant_manager",
    label: "Assistant Manager",
    icon: faUserTie,
    description: "Supports management duties",
  },
  {
    value: "administrator",
    label: "Administrator",
    icon: faShieldAlt,
    description: "Handles administrative tasks",
  },
  {
    value: "community_relation",
    label: "Community & Farmer Relation",
    icon: faUsers,
    description: "Builds community and farmer relationships",
  },
  {
    value: "developer",
    label: "Developer",
    icon: faCode,
    description: "Develops and maintains systems",
  },
];

const LIVESTOCK_OPTIONS = [
  "Cattle",
  "Poultry",
  "Goats",
  "Sheep",
  "Pigs",
  "Mixed",
];

const BUSINESS_TYPES = [
  { value: "manufacturer", label: "Manufacturer" },
  { value: "distributor", label: "Distributor" },
  { value: "trader", label: "Trader" },
  { value: "broker", label: "Broker" },
];

const PRODUCT_CATEGORIES = [
  { value: "rumen-bypass-fat", label: "Rumen Bypass Fat" },
  { value: "mineral-blocks", label: "Mineral Blocks" },
  { value: "protein-concentrates", label: "Protein Concentrates" },
  { value: "feed-supplements", label: "Feed Supplements" },
  { value: "premixes", label: "Premixes" },
  { value: "veterinary-products", label: "Veterinary Products" },
];

const INCOTERMS_OPTIONS = ["FOB", "CIF", "EXW", "DAP", "DDP"];

// ============================================
// IMAGE UPLOAD COMPONENT
// ============================================
const ImageUploadField = ({
  label,
  icon,
  preview,
  onUpload,
  onRemove,
  inputRef,
  containerStyle = {},
  previewStyle = {},
  placeholderText = "Upload image",
  accept = "image/*",
  isCircle = true,
}) => (
  <div className="image-upload-field">
    <label className="image-upload-label">
      {icon && <FontAwesomeIcon icon={icon} />}
      {label}
    </label>
    <div className="image-upload-wrapper">
      <div
        className={`image-preview-container ${isCircle ? "circle" : "rectangle"}`}
        onClick={() => inputRef.current?.click()}
        style={{
          width: isCircle ? "120px" : "100%",
          height: isCircle ? "120px" : "100px",
          borderRadius: isCircle ? "50%" : "8px",
          border: "2px dashed #D9A321",
          overflow: "hidden",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8f9fa",
          position: "relative",
          transition: "all 0.3s ease",
          ...containerStyle,
        }}
      >
        {preview ? (
          <>
            <img
              src={preview}
              alt={label}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            {onRemove && (
              <button
                type="button"
                className="image-remove-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                aria-label="Remove image"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </>
        ) : (
          <div
            style={{ textAlign: "center", color: "#9ca3af", padding: "8px" }}
          >
            <FontAwesomeIcon
              icon={faCamera}
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "4px",
              }}
            />
            <span style={{ fontSize: "0.6rem" }}>{placeholderText}</span>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => {
          if (e.target.files?.[0]) {
            onUpload(e.target.files[0]);
          }
          e.target.value = "";
        }}
        style={{ display: "none" }}
      />
      <small>Click to upload {label.toLowerCase()} (optional)</small>
    </div>
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================
const Register = () => {
  const [userType, setUserType] = useState("customer");
  const [registrationMessage, setRegistrationMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Image refs
  const profileInputRef = useRef(null);
  const backgroundInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    address: { street: "", city: "", district: "" },
    farmDetails: {
      farmName: "",
      farmSize: "small",
      livestockType: [],
      animalCount: 0,
      yearsInOperation: 0,
    },
    preferredPaymentMethod: "cash_on_delivery",
    companyName: "",
    registrationNumber: "",
    taxId: "",
    businessType: "manufacturer",
    yearsInBusiness: 0,
    productCategories: [],
    supplierAddress: {
      street: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
    },
    supplierContact: {
      name: "",
      title: "",
      phone: "",
      whatsapp: "",
      alternatePhone: "",
    },
    shippingTerms: {
      incoterms: "FOB",
      portOfLoading: "",
      portOfDischarge: "Freetown, Sierra Leone",
      leadTime: 30,
    },
    paymentTerms: {
      method: "wire_transfer",
      depositPercent: 30,
      balanceTerms: "Before shipment",
    },
    documents: {
      certificateOfAnalysis: false,
      certificateOfOrigin: false,
      phytosanitary: false,
      fumigation: false,
      halal: false,
      iso: false,
    },
    isTeamMember: false,
    teamRole: "",
    // Image fields
    profileImage: null,
    backgroundImage: null,
    companyLogo: null,
    companyBanner: null,
  });

  // Image previews
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState(null);
  const [companyLogoPreview, setCompanyLogoPreview] = useState(null);
  const [companyBannerPreview, setCompanyBannerPreview] = useState(null);

  // ============================================
  // IMAGE UPLOAD HANDLERS
  // ============================================
  const handleImageUpload = (file, type) => {
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Image size must be less than 5MB");
      return;
    }

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setError("Please upload a valid image file (JPEG, PNG, GIF, WEBP)");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;

      switch (type) {
        case "profile":
          setProfileImagePreview(base64String);
          setFormData((prev) => ({ ...prev, profileImage: base64String }));
          break;
        case "background":
          setBackgroundImagePreview(base64String);
          setFormData((prev) => ({ ...prev, backgroundImage: base64String }));
          break;
        case "logo":
          setCompanyLogoPreview(base64String);
          setFormData((prev) => ({ ...prev, companyLogo: base64String }));
          break;
        case "banner":
          setCompanyBannerPreview(base64String);
          setFormData((prev) => ({ ...prev, companyBanner: base64String }));
          break;
        default:
          break;
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = (type) => {
    switch (type) {
      case "profile":
        setProfileImagePreview(null);
        setFormData((prev) => ({ ...prev, profileImage: null }));
        if (profileInputRef.current) profileInputRef.current.value = "";
        break;
      case "background":
        setBackgroundImagePreview(null);
        setFormData((prev) => ({ ...prev, backgroundImage: null }));
        if (backgroundInputRef.current) backgroundInputRef.current.value = "";
        break;
      case "logo":
        setCompanyLogoPreview(null);
        setFormData((prev) => ({ ...prev, companyLogo: null }));
        if (logoInputRef.current) logoInputRef.current.value = "";
        break;
      case "banner":
        setCompanyBannerPreview(null);
        setFormData((prev) => ({ ...prev, companyBanner: null }));
        if (bannerInputRef.current) bannerInputRef.current.value = "";
        break;
      default:
        break;
    }
  };

  // ============================================
  // FORM HANDLERS
  // ============================================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setError("");
  };

  const handleLivestockToggle = (type) => {
    setFormData((prev) => {
      const currentTypes = prev.farmDetails.livestockType;
      const newTypes = currentTypes.includes(type)
        ? currentTypes.filter((t) => t !== type)
        : [...currentTypes, type];
      return {
        ...prev,
        farmDetails: { ...prev.farmDetails, livestockType: newTypes },
      };
    });
  };

  const handleCategoryToggle = (category) => {
    setFormData((prev) => {
      const current = prev.productCategories;
      const newCategories = current.includes(category)
        ? current.filter((c) => c !== category)
        : [...current, category];
      return { ...prev, productCategories: newCategories };
    });
  };

  const handleDocumentToggle = (doc) => {
    setFormData((prev) => ({
      ...prev,
      documents: { ...prev.documents, [doc]: !prev.documents[doc] },
    }));
  };

  // ============================================
  // SUBMIT HANDLER
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (userType === "customer" && !formData.name) {
      setError("Full name is required");
      return;
    }
    if (userType === "farmer") {
      if (!formData.farmDetails.farmName) {
        setError("Farm name is required for farmer registration");
        return;
      }
      if (formData.farmDetails.livestockType.length === 0) {
        setError("Please select at least one livestock type");
        return;
      }
    }
    if (userType === "supplier") {
      if (!formData.companyName) {
        setError("Company name is required for supplier registration");
        return;
      }
      if (!formData.registrationNumber) {
        setError("Registration number is required");
        return;
      }
      if (!formData.supplierContact.name) {
        setError("Contact person name is required");
        return;
      }
      if (!formData.supplierAddress.country) {
        setError("Country is required for supplier address");
        return;
      }
      if (formData.productCategories.length === 0) {
        setError("Please select at least one product category");
        return;
      }
    }

    if (formData.isTeamMember && !formData.teamRole) {
      setError("Please select a team role");
      return;
    }

    const registrationData = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      phone: formData.phone || "",
      userType: formData.isTeamMember ? "team" : userType,
      role: formData.isTeamMember ? "team" : userType,
      isTeamMember: formData.isTeamMember,
      teamRole: formData.teamRole || "",
      // Include images
      profileImage: formData.profileImage,
      backgroundImage: formData.backgroundImage,
      companyLogo: formData.companyLogo,
      companyBanner: formData.companyBanner,
    };

    if (
      formData.address.street ||
      formData.address.city ||
      formData.address.district
    ) {
      registrationData.address = formData.address;
    }

    if (userType === "farmer") {
      registrationData.farmDetails = {
        farmName: formData.farmDetails.farmName,
        farmSize: formData.farmDetails.farmSize,
        livestockType: formData.farmDetails.livestockType,
        animalCount: parseInt(formData.farmDetails.animalCount) || 0,
        yearsInOperation: parseInt(formData.farmDetails.yearsInOperation) || 0,
      };
      registrationData.preferredPaymentMethod = formData.preferredPaymentMethod;
      registrationData.bulkPurchaseEnabled = true;
      registrationData.creditLimit =
        formData.farmDetails.farmSize === "large" ? 5000 : 1000;
    }

    if (userType === "supplier") {
      registrationData.supplierData = {
        companyName: formData.companyName,
        registrationNumber: formData.registrationNumber,
        taxId: formData.taxId || "",
        businessType: formData.businessType,
        yearsInBusiness: parseInt(formData.yearsInBusiness) || 0,
        productCategories: formData.productCategories,
        address: {
          street: formData.supplierAddress.street,
          city: formData.supplierAddress.city,
          state: formData.supplierAddress.state || "",
          country: formData.supplierAddress.country,
          postalCode: formData.supplierAddress.postalCode || "",
        },
        contactPerson: {
          name: formData.supplierContact.name,
          title: formData.supplierContact.title || "",
          phone: formData.supplierContact.phone || formData.phone,
          whatsapp: formData.supplierContact.whatsapp || "",
          alternatePhone: formData.supplierContact.alternatePhone || "",
        },
        shippingTerms: {
          incoterms: formData.shippingTerms.incoterms,
          portOfLoading: formData.shippingTerms.portOfLoading || "",
          portOfDischarge:
            formData.shippingTerms.portOfDischarge || "Freetown, Sierra Leone",
          leadTime: parseInt(formData.shippingTerms.leadTime) || 30,
        },
        paymentTerms: {
          method: formData.paymentTerms.method || "wire_transfer",
          depositPercent: parseInt(formData.paymentTerms.depositPercent) || 30,
          balanceTerms: formData.paymentTerms.balanceTerms || "Before shipment",
        },
        documents: formData.documents,
        isApproved: false,
      };
    }

    setLoading(true);
    try {
      const result = await register(registrationData);

      if (
        userType === "supplier" &&
        result.supplier &&
        !result.supplier.isApproved
      ) {
        setRegistrationMessage(
          "Your supplier registration has been submitted successfully! An administrator will review your application and approve your account. You will be notified once approved.",
        );
        setTimeout(() => navigate("/login"), 5000);
        return;
      }

      if (result.isTeamMember && !result.isApproved) {
        setRegistrationMessage(
          "Your team registration has been submitted! You will receive notification once an administrator approves your account.",
        );
        setTimeout(() => navigate("/login"), 5000);
        return;
      }

      navigate("/dashboard");
    } catch (err) {
      setError(
        err.response?.data?.message || "Registration failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Render user type icon
  const getUserTypeIcon = (type) => {
    switch (type) {
      case "customer":
        return faStore;
      case "farmer":
        return faTractor;
      case "supplier":
        return faBuilding;
      case "team":
        return faUserTie;
      default:
        return faUser;
    }
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <>
      <SEO
        title="Register - Kenali Enterprise"
        description="Create your Kenali Enterprise account to access premium livestock feed supplements, animal nutrition products, and farming solutions."
        keywords="register, Kenali Enterprise, livestock feed, farmer account, supplier registration"
        canonical="https://kenalienterprise.com/register"
      />
      <motion.div
        className="auth-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="auth-container auth-register">
          <div className="auth-header">
            <FontAwesomeIcon icon={faUser} className="auth-icon" />
            <h1>Create an Account</h1>
            <p>Join Kenali Enterprise today</p>
          </div>

          {error && (
            <div className="auth-error">
              <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
            </div>
          )}

          {registrationMessage && (
            <div className="auth-success">
              <FontAwesomeIcon icon={faCheckCircle} /> {registrationMessage}
            </div>
          )}

          <div className="user-type-selector">
            {["customer", "farmer", "supplier", "team"].map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => {
                  setUserType(type);
                  setFormData((prev) => ({
                    ...prev,
                    isTeamMember: type === "team",
                  }));
                }}
                className={`user-type-btn ${
                  (type === "team" && formData.isTeamMember) ||
                  (type !== "team" &&
                    userType === type &&
                    !formData.isTeamMember)
                    ? "active"
                    : ""
                }`}
              >
                <FontAwesomeIcon
                  icon={getUserTypeIcon(type)}
                  className="user-type-icon"
                />
                <span className="user-type-label">
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </span>
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            {/* ============================================
                BASIC INFO
                ============================================ */}
            <div className="form-group">
              <label>
                <FontAwesomeIcon icon={faUser} /> Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter your full name"
              />
            </div>

            <div className="form-group">
              <label>
                <FontAwesomeIcon icon={faEnvelope} /> Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="you@example.com"
              />
            </div>

            <div className="form-group">
              <label>
                <FontAwesomeIcon icon={faPhone} /> Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+232 76 123456"
              />
            </div>

            {/* ============================================
                PROFILE IMAGE UPLOAD - FOR ALL USERS
                ============================================ */}
            <div className="auth-section image-upload-section">
              <h3 className="auth-section-title">
                <FontAwesomeIcon icon={faImage} /> Profile Image
              </h3>
              <p className="auth-section-description">
                Upload a profile photo to personalize your account.
              </p>
              <ImageUploadField
                label="Profile Photo"
                icon={faCamera}
                preview={profileImagePreview}
                onUpload={(file) => handleImageUpload(file, "profile")}
                onRemove={() => handleRemoveImage("profile")}
                inputRef={profileInputRef}
                placeholderText="Add photo"
                isCircle={true}
              />
            </div>

            {/* ============================================
                BACKGROUND IMAGE - FOR ALL USERS
                ============================================ */}
            <div className="auth-section image-upload-section">
              <h3 className="auth-section-title">
                <FontAwesomeIcon icon={faImage} /> Background Image
              </h3>
              <p className="auth-section-description">
                Upload a background image for your profile.
              </p>
              <ImageUploadField
                label="Background Image"
                icon={faUpload}
                preview={backgroundImagePreview}
                onUpload={(file) => handleImageUpload(file, "background")}
                onRemove={() => handleRemoveImage("background")}
                inputRef={backgroundInputRef}
                placeholderText="Upload background"
                isCircle={false}
                containerStyle={{ height: "100px" }}
              />
            </div>

            {/* ============================================
                TEAM ROLE SELECTION
                ============================================ */}
            {formData.isTeamMember && (
              <div className="auth-section">
                <h3 className="auth-section-title">
                  <FontAwesomeIcon icon={faUserTie} /> Team Role Selection
                </h3>
                <p className="auth-section-description">
                  Select your team role. Your registration will be reviewed by
                  an administrator before you can login.
                </p>
                <div className="team-role-grid">
                  {TEAM_ROLES.map((role) => (
                    <button
                      key={role.value}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          teamRole: role.value,
                        }))
                      }
                      className={`team-role-btn ${formData.teamRole === role.value ? "active" : ""}`}
                    >
                      <FontAwesomeIcon icon={role.icon} className="role-icon" />
                      <div className="role-info">
                        <div className="role-label">{role.label}</div>
                        <div className="role-description">
                          {role.description}
                        </div>
                      </div>
                      {formData.teamRole === role.value && (
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="role-check"
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ============================================
                ADDRESS - CUSTOMER / FARMER
                ============================================ */}
            {(userType === "customer" || userType === "farmer") &&
              !formData.isTeamMember && (
                <>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faMapMarkerAlt} /> Street Address
                    </label>
                    <input
                      type="text"
                      name="address.street"
                      value={formData.address.street}
                      onChange={handleChange}
                      placeholder="Street address"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faCity} /> City
                      </label>
                      <input
                        type="text"
                        name="address.city"
                        value={formData.address.city}
                        onChange={handleChange}
                        placeholder="City"
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faLocationDot} /> District
                      </label>
                      <input
                        type="text"
                        name="address.district"
                        value={formData.address.district}
                        onChange={handleChange}
                        placeholder="District"
                      />
                    </div>
                  </div>
                </>
              )}

            {/* ============================================
                FARMER SPECIFIC
                ============================================ */}
            {userType === "farmer" && !formData.isTeamMember && (
              <div className="auth-section">
                <h3 className="auth-section-title">
                  <FontAwesomeIcon icon={faTractor} /> Farm Details
                </h3>
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faStore} /> Farm Name *
                  </label>
                  <input
                    type="text"
                    name="farmDetails.farmName"
                    value={formData.farmDetails.farmName}
                    onChange={handleChange}
                    placeholder="e.g., Green Acres Farm"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faWarehouse} /> Farm Size
                  </label>
                  <select
                    name="farmDetails.farmSize"
                    value={formData.farmDetails.farmSize}
                    onChange={handleChange}
                  >
                    <option value="small">Small (1-20 animals)</option>
                    <option value="medium">Medium (21-50 animals)</option>
                    <option value="large">Large (50+ animals)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faUsers} /> Livestock Type *
                  </label>
                  <div className="checkbox-group">
                    {LIVESTOCK_OPTIONS.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => handleLivestockToggle(type)}
                        className={`checkbox-btn ${formData.farmDetails.livestockType.includes(type) ? "active" : ""}`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faTag} /> Animal Count
                    </label>
                    <input
                      type="number"
                      name="farmDetails.animalCount"
                      value={formData.farmDetails.animalCount}
                      onChange={handleChange}
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faClock} /> Years in Operation
                    </label>
                    <input
                      type="number"
                      name="farmDetails.yearsInOperation"
                      value={formData.farmDetails.yearsInOperation}
                      onChange={handleChange}
                      min="0"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faDollarSign} /> Preferred Payment
                  </label>
                  <select
                    name="preferredPaymentMethod"
                    value={formData.preferredPaymentMethod}
                    onChange={handleChange}
                  >
                    <option value="cash_on_delivery">Cash on Delivery</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="credit">Credit</option>
                  </select>
                </div>
              </div>
            )}

            {/* ============================================
                SUPPLIER SPECIFIC - WITH LOGO & BANNER
                ============================================ */}
            {userType === "supplier" && !formData.isTeamMember && (
              <div className="auth-section">
                <h3 className="auth-section-title">
                  <FontAwesomeIcon icon={faBuilding} /> Company Information
                </h3>
                <p className="auth-section-description">
                  Provide your company details for supplier registration. Your
                  account will be reviewed by an administrator before approval.
                </p>

                {/* ============================================
                    SUPPLIER LOGO UPLOAD
                    ============================================ */}
                <div className="auth-section image-upload-section">
                  <h4 className="sub-section-title">
                    <FontAwesomeIcon icon={faImage} /> Company Logo
                  </h4>
                  <ImageUploadField
                    label="Company Logo"
                    icon={faCamera}
                    preview={companyLogoPreview}
                    onUpload={(file) => handleImageUpload(file, "logo")}
                    onRemove={() => handleRemoveImage("logo")}
                    inputRef={logoInputRef}
                    placeholderText="Add logo"
                    isCircle={true}
                    containerStyle={{
                      borderRadius: "8px",
                      width: "120px",
                      height: "120px",
                    }}
                  />
                </div>

                {/* ============================================
                    SUPPLIER BANNER UPLOAD
                    ============================================ */}
                <div className="auth-section image-upload-section">
                  <h4 className="sub-section-title">
                    <FontAwesomeIcon icon={faImage} /> Company Banner
                  </h4>
                  <ImageUploadField
                    label="Company Banner"
                    icon={faUpload}
                    preview={companyBannerPreview}
                    onUpload={(file) => handleImageUpload(file, "banner")}
                    onRemove={() => handleRemoveImage("banner")}
                    inputRef={bannerInputRef}
                    placeholderText="Upload banner"
                    isCircle={false}
                    containerStyle={{ height: "100px" }}
                  />
                </div>

                {/* Company Details */}
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faBuilding} /> Company Name *
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="e.g., Global Feed Supply Ltd"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faCertificate} /> Registration Number
                    *
                  </label>
                  <input
                    type="text"
                    name="registrationNumber"
                    value={formData.registrationNumber}
                    onChange={handleChange}
                    placeholder="e.g., SL-2024-001"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faIdCard} /> Tax ID (Optional)
                  </label>
                  <input
                    type="text"
                    name="taxId"
                    value={formData.taxId}
                    onChange={handleChange}
                    placeholder="e.g., TIN-123456"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faBriefcase} /> Business Type
                    </label>
                    <select
                      name="businessType"
                      value={formData.businessType}
                      onChange={handleChange}
                    >
                      {BUSINESS_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faClock} /> Years in Business
                    </label>
                    <input
                      type="number"
                      name="yearsInBusiness"
                      value={formData.yearsInBusiness}
                      onChange={handleChange}
                      min="0"
                      placeholder="0"
                    />
                  </div>
                </div>

                <h4 className="sub-section-title">
                  <FontAwesomeIcon icon={faMapMarkerAlt} /> Company Address *
                </h4>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faLocationDot} /> Street Address *
                  </label>
                  <input
                    type="text"
                    name="supplierAddress.street"
                    value={formData.supplierAddress.street}
                    onChange={handleChange}
                    placeholder="Street address"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faCity} /> City *
                    </label>
                    <input
                      type="text"
                      name="supplierAddress.city"
                      value={formData.supplierAddress.city}
                      onChange={handleChange}
                      placeholder="City"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faLocationDot} /> State/Province
                    </label>
                    <input
                      type="text"
                      name="supplierAddress.state"
                      value={formData.supplierAddress.state}
                      onChange={handleChange}
                      placeholder="State"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faGlobe} /> Country *
                    </label>
                    <input
                      type="text"
                      name="supplierAddress.country"
                      value={formData.supplierAddress.country}
                      onChange={handleChange}
                      placeholder="Country"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faIdCard} /> Postal Code
                    </label>
                    <input
                      type="text"
                      name="supplierAddress.postalCode"
                      value={formData.supplierAddress.postalCode}
                      onChange={handleChange}
                      placeholder="Postal code"
                    />
                  </div>
                </div>

                <h4 className="sub-section-title">
                  <FontAwesomeIcon icon={faUserTie} /> Contact Person *
                </h4>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faUser} /> Contact Name *
                  </label>
                  <input
                    type="text"
                    name="supplierContact.name"
                    value={formData.supplierContact.name}
                    onChange={handleChange}
                    placeholder="Full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faBriefcase} /> Job Title
                  </label>
                  <input
                    type="text"
                    name="supplierContact.title"
                    value={formData.supplierContact.title}
                    onChange={handleChange}
                    placeholder="e.g., Sales Manager"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faPhone} /> Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="supplierContact.phone"
                      value={formData.supplierContact.phone}
                      onChange={handleChange}
                      placeholder="+232 76 123456"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faPhoneAlt} /> Alternate Phone
                    </label>
                    <input
                      type="tel"
                      name="supplierContact.alternatePhone"
                      value={formData.supplierContact.alternatePhone}
                      onChange={handleChange}
                      placeholder="+232 76 123456"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp
                  </label>
                  <input
                    type="tel"
                    name="supplierContact.whatsapp"
                    value={formData.supplierContact.whatsapp}
                    onChange={handleChange}
                    placeholder="+232 76 123456"
                  />
                </div>

                <h4 className="sub-section-title">
                  <FontAwesomeIcon icon={faBox} /> Product Categories *
                </h4>
                <p className="auth-section-description">
                  Select at least one product category you supply
                </p>

                <div className="checkbox-grid">
                  {PRODUCT_CATEGORIES.map((cat) => (
                    <label key={cat.value} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.productCategories.includes(cat.value)}
                        onChange={() => handleCategoryToggle(cat.value)}
                      />
                      <FontAwesomeIcon icon={faTag} />
                      {cat.label}
                    </label>
                  ))}
                </div>

                <h4 className="sub-section-title">
                  <FontAwesomeIcon icon={faShip} /> Shipping Terms
                </h4>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faTruck} /> Incoterms
                  </label>
                  <select
                    name="shippingTerms.incoterms"
                    value={formData.shippingTerms.incoterms}
                    onChange={handleChange}
                  >
                    {INCOTERMS_OPTIONS.map((term) => (
                      <option key={term} value={term}>
                        {term}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faShip} /> Port of Loading
                    </label>
                    <input
                      type="text"
                      name="shippingTerms.portOfLoading"
                      value={formData.shippingTerms.portOfLoading}
                      onChange={handleChange}
                      placeholder="e.g., Port Klang, Malaysia"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faClock} /> Lead Time (Days)
                    </label>
                    <input
                      type="number"
                      name="shippingTerms.leadTime"
                      value={formData.shippingTerms.leadTime}
                      onChange={handleChange}
                      min="1"
                      placeholder="30"
                    />
                  </div>
                </div>

                <h4 className="sub-section-title">
                  <FontAwesomeIcon icon={faMoneyBillTransfer} /> Payment Terms
                </h4>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faDollarSign} /> Payment Method
                    </label>
                    <select
                      name="paymentTerms.method"
                      value={formData.paymentTerms.method}
                      onChange={handleChange}
                    >
                      <option value="wire_transfer">Wire Transfer</option>
                      <option value="letter_of_credit">Letter of Credit</option>
                      <option value="paypal">PayPal</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faPercent} /> Deposit %
                    </label>
                    <input
                      type="number"
                      name="paymentTerms.depositPercent"
                      value={formData.paymentTerms.depositPercent}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      placeholder="30"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faFileAlt} /> Balance Terms
                  </label>
                  <input
                    type="text"
                    name="paymentTerms.balanceTerms"
                    value={formData.paymentTerms.balanceTerms}
                    onChange={handleChange}
                    placeholder="e.g., Before shipment"
                  />
                </div>

                <h4 className="sub-section-title">
                  <FontAwesomeIcon icon={faFileAlt} /> Documents &
                  Certifications
                </h4>
                <div className="checkbox-grid">
                  {Object.entries(formData.documents).map(([key, value]) => (
                    <label key={key} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={() => handleDocumentToggle(key)}
                      />
                      <FontAwesomeIcon icon={faCertificate} />
                      {key
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* ============================================
                PASSWORD
                ============================================ */}
            <div className="form-group">
              <label>
                <FontAwesomeIcon icon={faLock} /> Password *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength="6"
                placeholder="Min 6 characters"
              />
            </div>

            <div className="form-group">
              <label>
                <FontAwesomeIcon icon={faLock} /> Confirm Password *
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                minLength="6"
                placeholder="Confirm your password"
              />
            </div>

            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Creating Account...
                </>
              ) : (
                <>
                  Register as{" "}
                  {formData.isTeamMember
                    ? "Team Member"
                    : userType.charAt(0).toUpperCase() + userType.slice(1)}{" "}
                  <FontAwesomeIcon icon={faArrowRight} />
                </>
              )}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              Already have an account? <Link to="/login">Login here</Link>
            </p>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default Register;
